import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, getDay, subMonths, addMonths, isSameMonth } from 'date-fns';
import { ChevronLeft, ChevronRight, CheckCircle2, Circle, X, Star } from 'lucide-react';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function CalendarHeatmap({ calendarData, onDayClick }) {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const startPadding = getDay(monthStart);

  const getDayStatus = (dateStr) => {
    const day = calendarData.find(d => d.date === dateStr);
    return day?.status || 'none';
  };

  const getColor = (status) => {
    switch (status) {
      case 'complete': return 'bg-[--hc-accent] hover:ring-2 hover:ring-[--hc-accent]/40';
      case 'partial': return 'bg-[#a7b8d3] hover:ring-2 hover:ring-[#a7b8d3]/40';
      default: return 'bg-[#e8edf4] hover:ring-2 hover:ring-[--hc-border]';
    }
  };

  return (
    <div data-testid="history-heatmap">
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => setCurrentMonth(prev => subMonths(prev, 1))}
          className="p-1.5 rounded-lg hover:bg-[--hc-surface] text-[--hc-text-muted] transition-colors"
        >
          <ChevronLeft size={18} />
        </button>
        <h3 className="text-sm font-semibold text-[--hc-text]">
          {format(currentMonth, 'MMMM yyyy')}
        </h3>
        <button
          onClick={() => setCurrentMonth(prev => addMonths(prev, 1))}
          className="p-1.5 rounded-lg hover:bg-[--hc-surface] text-[--hc-text-muted] transition-colors"
        >
          <ChevronRight size={18} />
        </button>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 mb-4 text-xs text-[--hc-text-muted]" data-testid="heatmap-legend">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-[#e8edf4]" />
          <span>No activity</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-[#a7b8d3]" />
          <span>Partial</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-[--hc-accent]" />
          <span>Complete</span>
        </div>
      </div>

      {/* Weekday headers */}
      <div className="grid grid-cols-7 gap-1 mb-1">
        {WEEKDAYS.map(day => (
          <div key={day} className="text-center text-xs text-[--hc-text-muted] py-1">{day}</div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1">
        {Array.from({ length: startPadding }).map((_, i) => (
          <div key={`pad-${i}`} />
        ))}
        {days.map(day => {
          const dateStr = format(day, 'yyyy-MM-dd');
          const status = getDayStatus(dateStr);
          const isToday = dateStr === format(new Date(), 'yyyy-MM-dd');

          return (
            <button
              key={dateStr}
              data-testid={`heatmap-day-${dateStr}`}
              onClick={() => onDayClick(dateStr)}
              className={`aspect-square rounded-md ${getColor(status)} transition-all cursor-pointer flex items-center justify-center text-xs ${
                isToday ? 'ring-2 ring-[--hc-primary]' : ''
              }`}
              aria-label={`${format(day, 'MMMM d')}, ${status}`}
            >
              <span className={`${status === 'complete' ? 'text-[--hc-on-accent] font-medium' : status === 'partial' ? 'text-white font-medium' : 'text-[--hc-text-muted]'}`}>
                {format(day, 'd')}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function DayDetailModal({ dateStr, onClose }) {
  const [dayLog, setDayLog] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/history/day/${dateStr}`)
      .then(res => {
        setDayLog(res.data.log);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [dateStr]);

  // Escape key handler
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="day-detail-overlay"
    >
      <div
        className="bg-white rounded-xl max-w-[480px] w-full max-h-[80vh] overflow-y-auto shadow-xl relative z-[61]"
        onClick={(e) => e.stopPropagation()}
        data-testid="day-detail-modal"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-[--hc-border]">
          <h3 className="text-sm font-semibold text-[--hc-text]">
            {format(new Date(dateStr + 'T12:00:00'), 'EEEE, MMMM d, yyyy')}
          </h3>
          <button onClick={onClose} data-testid="day-detail-close" className="text-[--hc-text-muted] hover:text-[--hc-text] p-1 rounded-lg hover:bg-[--hc-surface]">
            <X size={18} />
          </button>
        </div>

        <div className="p-4">
          {loading && <p className="text-sm text-[--hc-text-muted]">Loading...</p>}

          {!loading && !dayLog && (
            <p className="text-sm text-[--hc-text-muted] text-center py-6">No activity recorded for this day.</p>
          )}

          {!loading && dayLog && (
            <>
              <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium mb-4 ${
                dayLog.status === 'complete' ? 'bg-[--hc-accent-soft] text-[--hc-on-accent]' :
                dayLog.status === 'partial' ? 'bg-[--hc-surface-2] text-[--hc-text]' :
                'bg-[--hc-surface] text-[--hc-text-muted]'
              }`}>
                {dayLog.status === 'complete' ? 'Complete' : dayLog.status === 'partial' ? 'Partial' : 'No activity'}
              </div>

              {dayLog.routine_items?.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-xs font-semibold text-[--hc-text-muted] uppercase tracking-wide mb-2">Routine</h4>
                  <div className="space-y-1.5">
                    {dayLog.routine_items.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm">
                        {item.completed ? (
                          <CheckCircle2 size={16} className="text-[--hc-accent] flex-shrink-0" />
                        ) : (
                          <Circle size={16} className="text-[--hc-border] flex-shrink-0" />
                        )}
                        <span className={item.completed ? 'text-[--hc-text-muted]' : 'text-[--hc-text]'}>{item.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {dayLog.operating_actions?.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-[--hc-text-muted] uppercase tracking-wide mb-2">Operating Actions</h4>
                  <div className="space-y-1.5">
                    {dayLog.operating_actions.map((action, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm">
                        {action.completed ? (
                          <CheckCircle2 size={16} className="text-[--hc-accent] flex-shrink-0" />
                        ) : (
                          <Circle size={16} className="text-[--hc-border] flex-shrink-0" />
                        )}
                        <span className={action.completed ? 'text-[--hc-text-muted]' : 'text-[--hc-text]'}>{action.title}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function HistoryPage() {
  const [calendarData, setCalendarData] = useState([]);
  const [weeklyReviews, setWeeklyReviews] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('calendar');

  useEffect(() => {
    Promise.all([
      api.get('/history/calendar'),
      api.get('/weekly-reviews'),
    ]).then(([calRes, revRes]) => {
      setCalendarData(calRes.data.days || []);
      setWeeklyReviews(revRes.data.reviews || []);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-[--hc-text-muted] text-sm">Loading history...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-[--hc-text]">History</h2>
        <p className="text-sm text-[--hc-text-muted]">Track your consistency over time</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 rounded-xl bg-[--hc-surface] w-fit">
        <button
          onClick={() => setActiveTab('calendar')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'calendar' ? 'bg-white text-[--hc-primary] shadow-sm' : 'text-[--hc-text-muted] hover:text-[--hc-text]'
          }`}
        >
          Calendar
        </button>
        <button
          onClick={() => setActiveTab('reviews')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'reviews' ? 'bg-white text-[--hc-primary] shadow-sm' : 'text-[--hc-text-muted] hover:text-[--hc-text]'
          }`}
        >
          Weekly Reviews
        </button>
      </div>

      {activeTab === 'calendar' && (
        <div className="rounded-xl border border-[--hc-border] bg-white p-4 md:p-6">
          <CalendarHeatmap
            calendarData={calendarData}
            onDayClick={(dateStr) => setSelectedDay(dateStr)}
          />
        </div>
      )}

      {activeTab === 'reviews' && (
        <div data-testid="weekly-reviews-list">
          {weeklyReviews.length === 0 ? (
            <div className="text-center py-12 text-[--hc-text-muted] text-sm">
              <p>No weekly reviews yet.</p>
              <p className="text-xs mt-1">Your first review will appear after you complete one.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {weeklyReviews.map(review => (
                <div key={review.id} className="rounded-xl border border-[--hc-border] bg-white px-4 py-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-[--hc-text]">
                      Week of {format(new Date(review.week_start_date + 'T12:00:00'), 'MMM d, yyyy')}
                    </span>
                    <div className="flex items-center gap-1">
                      <Star size={14} className="text-[--hc-accent]" />
                      <span className="text-sm font-bold text-[--hc-primary] tabular-nums">{review.consistency_score}/10</span>
                    </div>
                  </div>
                  {review.obstacles && (
                    <div className="mb-1.5">
                      <span className="text-xs text-[--hc-text-muted]">What got in the way: </span>
                      <span className="text-xs text-[--hc-text]">{review.obstacles}</span>
                    </div>
                  )}
                  {review.commitment && (
                    <div>
                      <span className="text-xs text-[--hc-text-muted]">Commitment: </span>
                      <span className="text-xs text-[--hc-text]">{review.commitment}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Day Detail Modal */}
      {selectedDay && (
        <DayDetailModal
          dateStr={selectedDay}
          onClose={() => setSelectedDay(null)}
        />
      )}
    </div>
  );
}
